import apiClient from './api';

// ==================== Type Definitions ====================

export type ChannelStatus = 'stopped' | 'starting' | 'running' | 'stopping' | 'error';
export type OutputFormat = 'hls' | 'rtmp' | 'both';
export type TranscodePreset = 'ultrafast' | 'superfast' | 'faster' | 'fast' | 'medium';
export type OutputResolution = '720p' | '1080p' | 'adaptive';
export type LogoPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type HlsBitrateConfig = 'amagi' | '720p' | '1080p' | '720p_1080p';

export interface Channel {
  id: string;
  user_id: string;
  engine_id: string | null;
  name: string;
  description: string | null;

  // Output settings
  output_format: OutputFormat;
  transcode_preset: TranscodePreset;
  output_resolution: OutputResolution;

  // HLS settings
  hls_segment_duration: number;
  hls_playlist_length: number;
  dvr_window: number;
  low_latency_mode: boolean;

  // RTMP settings
  rtmp_url: string | null;
  rtmp_key: string | null;

  // YouTube Push settings
  push_to_youtube: boolean;
  youtube_stream_key: string | null;
  youtube_push_status: 'stopped' | 'starting' | 'running' | 'stopping' | 'error';

  // Branding
  logo_path: string | null;
  logo_position: LogoPosition;
  show_now_playing: boolean;
  show_up_next: boolean;

  // Ad/Slate settings
  ad_interval: number;
  ad_duration: number;
  slate_path: string | null;
  hls_bitrate_config: HlsBitrateConfig;

  // Looping settings
  loop_last_template: boolean;

  // Status
  status: ChannelStatus;
  status_message: string | null;

  // Stream URLs
  hls_master_url: string | null;
  hls_720p_url: string | null;
  hls_1080p_url: string | null;

  // Metrics
  current_viewers: number;
  total_watch_time: string;

  // Additional settings (JSONB)
  settings: Record<string, any> | null;

  // Timestamps
  created_at: string;
  updated_at: string;
  started_at: string | null;
  stopped_at: string | null;
}

export interface CreateChannelDto {
  name: string;
  description?: string;
  output_format?: OutputFormat;
  transcode_preset?: TranscodePreset;
  output_resolution?: OutputResolution;
  hls_segment_duration?: number;
  hls_playlist_length?: number;
  dvr_window?: number;
  low_latency_mode?: boolean;
  rtmp_url?: string;
  rtmp_key?: string;
  push_to_youtube?: boolean;
  youtube_stream_key?: string;
  youtube_push_status?: 'stopped' | 'starting' | 'running' | 'stopping' | 'error';
  logo_path?: string;
  logo_position?: LogoPosition;
  show_now_playing?: boolean;
  show_up_next?: boolean;
  ad_interval?: number;
  ad_duration?: number;
  slate_path?: string;
  hls_bitrate_config?: HlsBitrateConfig;
  loop_last_template?: boolean;
  settings?: Record<string, any>;
}

export interface UpdateChannelDto {
  name?: string;
  description?: string;
  output_format?: OutputFormat;
  transcode_preset?: TranscodePreset;
  output_resolution?: OutputResolution;
  hls_segment_duration?: number;
  hls_playlist_length?: number;
  dvr_window?: number;
  low_latency_mode?: boolean;
  rtmp_url?: string;
  rtmp_key?: string;
  push_to_youtube?: boolean;
  youtube_stream_key?: string;
  youtube_push_status?: 'stopped' | 'starting' | 'running' | 'stopping' | 'error';
  logo_path?: string;
  logo_position?: LogoPosition;
  show_now_playing?: boolean;
  show_up_next?: boolean;
  ad_interval?: number;
  ad_duration?: number;
  slate_path?: string;
  hls_bitrate_config?: HlsBitrateConfig;
  loop_last_template?: boolean;
  settings?: Record<string, any>;
}

export interface ListChannelsParams {
  page?: number;
  limit?: number;
  status?: ChannelStatus;
  output_format?: OutputFormat;
  sort_by?: 'name' | 'created_at' | 'status' | 'updated_at';
  sort_order?: 'asc' | 'desc';
}

export interface ChannelListResponse {
  channels: Channel[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ChannelStats {
  total_channels: string;
  running_channels: string;
  stopped_channels: string;
  error_channels: string;
  total_viewers: string;
  total_watch_time: string;
}

export interface UpdateChannelStatusDto {
  status: ChannelStatus;
  status_message?: string;
}

export interface AssignEngineDto {
  engine_id: string;
}

// ==================== API Service ====================

const channelService = {
  /**
   * Create a new channel
   */
  createChannel: async (data: CreateChannelDto): Promise<Channel> => {
    const response = await apiClient.post<{ data: { channel: Channel } }>(
      '/channels',
      data
    );
    return response.data.data.channel;
  },

  /**
   * List all channels for the current user
   */
  listChannels: async (params?: ListChannelsParams): Promise<ChannelListResponse> => {
    const response = await apiClient.get<{ data: ChannelListResponse }>(
      '/channels',
      { params }
    );
    return response.data.data;
  },

  /**
   * Get channel statistics
   */
  getChannelStats: async (): Promise<ChannelStats> => {
    const response = await apiClient.get<{ data: ChannelStats }>('/channels/stats');
    return response.data.data;
  },

  /**
   * Get a single channel by ID
   */
  getChannel: async (id: string): Promise<Channel> => {
    const response = await apiClient.get<{ data: { channel: Channel } }>(
      `/channels/${id}`
    );
    return response.data.data.channel;
  },

  /**
   * Update a channel
   */
  updateChannel: async (id: string, data: UpdateChannelDto): Promise<Channel> => {
    const response = await apiClient.put<{ data: { channel: Channel } }>(
      `/channels/${id}`,
      data
    );
    return response.data.data.channel;
  },

  /**
   * Delete a channel
   */
  deleteChannel: async (id: string): Promise<void> => {
    await apiClient.delete(`/channels/${id}`);
  },

  /**
   * Update channel status
   */
  updateChannelStatus: async (
    id: string,
    data: UpdateChannelStatusDto
  ): Promise<Channel> => {
    const response = await apiClient.put<{ data: { channel: Channel } }>(
      `/channels/${id}/status`,
      data
    );
    return response.data.data.channel;
  },

  /**
   * Assign a playout engine to a channel (admin only)
   */
  assignEngine: async (id: string, data: AssignEngineDto): Promise<Channel> => {
    const response = await apiClient.post<{ data: { channel: Channel } }>(
      `/channels/${id}/assign-engine`,
      data
    );
    return response.data.data.channel;
  },

  /**
   * Start a channel (convenience method)
   */
  startChannel: async (id: string): Promise<Channel> => {
    return channelService.updateChannelStatus(id, { status: 'starting' });
  },

  /**
   * Stop a channel (convenience method)
   */
  stopChannel: async (id: string): Promise<Channel> => {
    return channelService.updateChannelStatus(id, { status: 'stopping' });
  },

  /**
   * Upload logo for a channel
   */
  uploadLogo: async (id: string, file: File): Promise<{ channel: Channel; logo_url: string }> => {
    const formData = new FormData();
    formData.append('logo', file);

    const response = await apiClient.post<{ data: { channel: Channel; logo_url: string } }>(
      `/channels/${id}/upload-logo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  },

  /**
   * Upload slate video for a channel
   */
  uploadSlate: async (id: string, file: File): Promise<{ channel: Channel; slate_url: string }> => {
    const formData = new FormData();
    formData.append('slate', file);

    const response = await apiClient.post<{ data: { channel: Channel; slate_url: string } }>(
      `/channels/${id}/upload-slate`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  },

  /**
   * Delete all channels (admin only - cleanup function)
   */
  deleteAllChannels: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.delete<{ data: { deletedCount: number } }>(
      '/channels/all'
    );
    return response.data.data;
  },
};

export default channelService;
